import React, { useEffect, useState } from "react";
import { connect, useSelector } from "react-redux";
import CoursesListView from "../../../../views/Library/AllCourses/CoursesList";
import {
  onlineCoursesSortMethods,
  webinarSortMethods,
  myCoursesSortMethods,
  iltSortMethods,
  virtualSortMethods,
  courseTypes,
  policiesSortMethods,
  packageTypes
} from "../../../../constants";
import LoadingSpinner from "../../../../components/LoadingSpinner/LoadingSpinner";
import {
  libraryAllCoursesOnline,
  libraryAllCoursesWebinar,
  libraryAllCoursesInPerson,
  libraryAllCoursesVirtual,
  learnLibraryAllPolicies
} from "../../../../routes/LearningCenterRoutes";
import { makeGetEntities } from "../../../../redux/selectors/selectors";
import { useCourses } from "../../../../hooks/learningCenter/myLibrary/courses";
import { useCoursePackages } from "../../../../hooks/learningCenter/coursePackages";

const selector = makeGetEntities();

const perPageOptions = ["12", "24", "36"];

const CoursesListContainer = ({ match, totalCount, loading }) => {
  const [coursesParams, setCoursesParams] = useState({
    courseType: "",
    sortByValue: null,
    page: 0,
    perPage: perPageOptions[0],
    filters: { coursePackageIdEq: "" }
  });
  const { fetchCoursePackages } = useCoursePackages();
  const [sortByOptions, setSortByOptions] = useState(null);
  const { fetchCourses } = useCourses();
  const [packageFilterOptions, setPackageFilterOptions] = useState([]);

  const courses = useSelector(({ userAssignmentsListState }) => {
    if (coursesParams.courseType) {
      return selector(userAssignmentsListState, {
        type: "version",
        id: userAssignmentsListState.displayedCourses[coursesParams.courseType]
      });
    } else {
      return [];
    }
  });

  function getSortByOptions(courseType) {
    if (
      courseTypes &&
      courseType.toLowerCase() === courseTypes.online.toLowerCase()
    ) {
      return onlineCoursesSortMethods;
    } else if (
      courseType &&
      courseType.toLowerCase() === courseTypes.webinar.toLowerCase()
    ) {
      return webinarSortMethods;
    } else if (
      courseType &&
      courseType.toLowerCase() === courseTypes.inPerson.toLowerCase()
    ) {
      return iltSortMethods;
    } else if (
      courseType &&
      courseType.toLowerCase() === courseTypes.virtual.toLowerCase()
    ) {
      return virtualSortMethods;
    } else if (
      courseType &&
      courseType.toLowerCase() === courseTypes.policy.toLowerCase()
    ) {
      return policiesSortMethods;
    } else {
      return myCoursesSortMethods;
    }
  }

  function getCourseType(path) {
    switch (path) {
      case learnLibraryAllPolicies:
        return courseTypes.policy.toLowerCase();
      case libraryAllCoursesOnline:
        return courseTypes.online.toLowerCase();
      case libraryAllCoursesWebinar:
        return courseTypes.webinar.toLowerCase();
      case libraryAllCoursesInPerson:
        return courseTypes.inPerson.toLowerCase();
      case libraryAllCoursesVirtual:
        return courseTypes.virtual.toLowerCase();
      default:
        return "online";
    }
  }

  useEffect(() => {
    fetchCoursePackages({
      filters: { packageTypeEq: packageTypes.manual }
    }).then(coursePackages => setPackageFilterOptions(coursePackages));
  }, []);

  useEffect(() => {
    const currentCourseType = getCourseType(match.path);
    const currentSortByOptions = getSortByOptions(currentCourseType);
    setSortByOptions(currentSortByOptions);
    setCoursesParams({
      ...coursesParams,
      courseType: currentCourseType,
      sortByValue: currentSortByOptions[0]
    });
  }, [match.path]);

  const { sortByValue, page, perPage, courseType } = coursesParams;

  const fetchSortedCourses = () => {
    if (sortByValue) {
      fetchCourses({
        params: { order: sortByValue.value, type: courseType },
        page: { number: page + 1, size: perPage },
        include: [
          "versions",
          "ilt_info",
          "ilt_info.instructor",
          "ilt_info.training_location",
          "user_assignment.enrolled_training_object.ilt_info.instructor",
          "user_assignment.enrolled_training_object.ilt_info.training_location"
        ],
        externalFilters: coursesParams.filters
      });
    }
  };

  useEffect(() => {
    fetchSortedCourses();
  }, [coursesParams]);

  const handleSortByValueChange = newSortByOptionValue => {
    let newSortByOption = sortByOptions.find(
      opt => opt.value === newSortByOptionValue.value
    );
    setCoursesParams({ ...coursesParams, sortByValue: newSortByOption });
  };

  const handlePerPageChange = perPage => {
    setCoursesParams({ ...coursesParams, perPage, page: 0 });
  };

  const handlePageChange = page => {
    setCoursesParams({ ...coursesParams, page });
  };

  const onFilterPackage = newPackageId => {
    setCoursesParams({
      ...coursesParams,
      filters: { coursePackageIdEq: newPackageId }
    });
  };

  if (loading) {
    return <LoadingSpinner visible={loading} />;
  } else if (!courses && !loading) {
    return <span>No courses for view</span>;
  } else {
    return (
      <CoursesListView
        packageFilterOptions={packageFilterOptions}
        packageFilterValue={coursesParams.filters.coursePackageIdEq}
        onFilterPackage={onFilterPackage}
        perPage={coursesParams.perPage}
        page={coursesParams.page}
        onPageChange={handlePageChange}
        onPerPageChange={handlePerPageChange}
        courses={courses}
        courseType={coursesParams.courseType}
        sortByOptions={sortByOptions}
        onSortOptionChange={handleSortByValueChange}
        sortByValue={coursesParams.sortByValue}
        perPageOptions={perPageOptions}
        totalCount={totalCount}
        refetchCourses={() => fetchSortedCourses()}
      />
    );
  }
};

const mapStateToProps = state => ({
  totalCount: state.userAssignmentsListState.totalCount,
  loading: state.userAssignmentsListState.loading
});

export default connect(mapStateToProps)(CoursesListContainer);
